export const DISCORD_LAYOUT = {
  phoneMax: 600,
  tabletMax: 840,
  serverRailWidth: 72,
  channelSidebarDefault: 304,
  channelSidebarMin: 240,
  channelSidebarMax: 360,
  memberSidebarWidth: 264,
  headerHeight: 48,
  desktopControlSize: 32,
  touchTargetSize: 44,
} as const;

export type ShellMode = "mobile" | "compact" | "desktop";
export type DiscordDensity = "spacious" | "default" | "compact";

const DENSITY_METRICS = {
  spacious: {
    messageSize: 16,
    messageGroupSpacing: 20,
    navigationRowHeight: 40,
  },
  default: {
    messageSize: 16,
    messageGroupSpacing: 16,
    navigationRowHeight: 34,
  },
  compact: {
    messageSize: 15,
    messageGroupSpacing: 8,
    navigationRowHeight: 30,
  },
} as const satisfies Record<DiscordDensity, object>;

export function getAuthFrameMetrics(viewportWidth: number) {
  const outerPadding = viewportWidth <= DISCORD_LAYOUT.phoneMax ? 20 : 35;

  return {
    outerPadding,
    contentWidth: Math.max(0, viewportWidth - outerPadding * 2),
  };
}

export function resolveShellMode(viewportWidth: number): ShellMode {
  if (viewportWidth <= DISCORD_LAYOUT.phoneMax) return "mobile";
  if (viewportWidth <= DISCORD_LAYOUT.tabletMax) return "compact";
  return "desktop";
}

export function shouldShowMemberSidebar(
  layout: "desktop" | "tablet" | "phone",
) {
  return layout === "desktop";
}

export function getShellViewportContract(viewportWidth: number) {
  const width = Math.max(0, Math.round(viewportWidth));
  const mode = resolveShellMode(width);
  const memberSidebarVisible = mode === "desktop";
  const drawerWidth = mode === "mobile" ? Math.max(0, width - 48) : 0;
  const fixedColumnsWidth =
    mode === "mobile"
      ? width
      : DISCORD_LAYOUT.serverRailWidth +
        DISCORD_LAYOUT.channelSidebarDefault +
        (memberSidebarVisible ? DISCORD_LAYOUT.memberSidebarWidth : 0);

  return {
    mode,
    drawerWidth,
    memberSidebarVisible,
    horizontalOverflow: Math.max(0, fixedColumnsWidth - width),
  };
}

export function clampChannelSidebarWidth(width: number) {
  return Math.min(
    DISCORD_LAYOUT.channelSidebarMax,
    Math.max(DISCORD_LAYOUT.channelSidebarMin, Math.round(width)),
  );
}

/**
 * Resolve a persisted channel sidebar width.
 *
 * The v2 key wins. A legacy 240px value was the old default rather than an
 * intentional resize, so it migrates to the new Discord-matched default.
 */
export function getChannelSidebarWidthFromStorage(
  storedWidth: string | null,
  legacyWidth: string | null,
) {
  if (storedWidth !== null) {
    const parsed = Number(storedWidth);
    return Number.isFinite(parsed) && parsed > 0
      ? clampChannelSidebarWidth(parsed)
      : DISCORD_LAYOUT.channelSidebarDefault;
  }

  const parsedLegacy = Number(legacyWidth);
  if (
    Number.isFinite(parsedLegacy) &&
    parsedLegacy > 0 &&
    parsedLegacy !== 240
  ) {
    return clampChannelSidebarWidth(parsedLegacy);
  }

  return DISCORD_LAYOUT.channelSidebarDefault;
}

export function getDensityMetrics(density: DiscordDensity) {
  return DENSITY_METRICS[density];
}
